/**
 * Your Privacy Choices — CCPA/CPRA preferences panel and footer entry point
 * (Asana 1219421406016853, D-106; re-scoped by D-126, approved D-127).
 *
 * Consent changes in exactly one place here: an explicit Save in the panel,
 * which writes Shopify's Customer Privacy API and sends one matching gtag
 * consent update. Nothing on this page's load path touches consent — no
 * replay, no end-of-load re-assert, and no cookie of our own. The Consent
 * Mode side of a Global Privacy Control signal is handled in the head by
 * snippets/privacy-choices-consent-state and privacy-choices-reassert,
 * before GTM and once after content_for_header.
 *
 * That is what keeps D-97 true: a US visitor who never interacts still gets
 * the theme default plus the Google & YouTube app grant, gcs=G111. The
 * Shopify consent API is loaded lazily, only when the panel is opened, so
 * even a passive page view with the panel on it reads nothing.
 *
 * /pages/data-sharing-opt-out is Shopify's managed data-sale opt-out page, so
 * the theme only links to it, as the footer link's no-JS fallback.
 *
 * Customer-facing strings here are frozen by the D-111 approval (copy review
 * section 1, items 20-24): do not reword them without a new approval.
 */
(function () {
  'use strict';

  if (window.__rsPrivacyChoices) return;
  window.__rsPrivacyChoices = true;

  var CATEGORIES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
  /* Categories a GPC signal turns off and locks. */
  var GPC_CATEGORIES = ['marketing', 'sale_of_data'];
  /* setTrackingConsent's callback does not fire if the request is blocked. */
  var SAVE_TIMEOUT_MS = 4000;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function gpcOn() {
    try {
      return navigator.globalPrivacyControl === true;
    } catch (e) {
      return false;
    }
  }

  /* ---------------------------------------------------------------- state */

  /** Loads Shopify's consent tracking API on demand. cb(available). */
  function withApi(cb) {
    var shopify = window.Shopify;
    try {
      if (shopify && shopify.customerPrivacy && typeof shopify.customerPrivacy.setTrackingConsent === 'function') {
        cb(true);
        return;
      }
      if (shopify && typeof shopify.loadFeatures === 'function') {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          cb(!err && !!(window.Shopify && window.Shopify.customerPrivacy));
        });
        return;
      }
    } catch (e) {
      /* fall through */
    }
    cb(false);
  }

  /**
   * What Shopify has stored for this visitor. null means "never set", which is
   * the state we must not disturb: it is the no-action visitor.
   */
  function storedConsent() {
    var out = { analytics: null, marketing: null, preferences: null, sale_of_data: null };
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.currentVisitorConsent === 'function') {
        var raw = cp.currentVisitorConsent() || {};
        CATEGORIES.forEach(function (key) {
          var value = raw[key];
          if (value === true || value === 'yes') out[key] = true;
          else if (value === false || value === 'no') out[key] = false;
        });
        return out;
      }

      /* The API has not loaded yet. The same decision is in the cookie it
         persists, so read that rather than showing the toggles as allowed:
         a saved deny rendered as an allow could be saved straight back as a
         grant the visitor never gave (#695). */
      var saved = window.RSConsentState && window.RSConsentState.read();
      if (saved) {
        CATEGORIES.forEach(function (key) {
          if (typeof saved[key] === 'boolean') out[key] = saved[key];
        });
      }
    } catch (e) {
      /* leave everything null */
    }
    return out;
  }

  /**
   * What the toggles show when Shopify has nothing stored. US visitors are not
   * opted out by default, so the categories read as allowed unless the cookie
   * banner was declined.
   */
  function fallbackValue() {
    try {
      if (window.RSConsent && window.RSConsent.get() === 'decline') return false;
    } catch (e) {
      /* ignore */
    }
    return true;
  }

  function resolveState() {
    var stored = storedConsent();
    var fallback = fallbackValue();
    var state = {};

    CATEGORIES.forEach(function (key) {
      state[key] = stored[key] !== null ? stored[key] : fallback;
    });

    if (gpcOn()) {
      GPC_CATEGORIES.forEach(function (key) {
        state[key] = false;
      });
    }
    return state;
  }

  function matchesStored(state) {
    var stored = storedConsent();
    return CATEGORIES.every(function (key) {
      return stored[key] === state[key];
    });
  }

  /* ---------------------------------------------------------------- write */

  function signal(allowed) {
    return allowed ? 'granted' : 'denied';
  }

  /**
   * Category → Consent Mode v2. Sale/sharing is the CCPA disclosure signal, so
   * it gates the two parameters Google treats as sending data to third parties
   * for advertising on top of marketing itself.
   */
  function gtagUpdate(state) {
    var shareable = state.marketing && state.sale_of_data;
    var payload = {
      analytics_storage: signal(state.analytics),
      ad_storage: signal(state.marketing),
      ad_user_data: signal(shareable),
      ad_personalization: signal(shareable),
      functionality_storage: signal(state.preferences),
      personalization_storage: signal(state.preferences)
    };

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', payload);
      return;
    }

    /* No global gtag on this page — the Zipify layout has no GTM block of
       ours. Queue it the way the re-apply snippet does, so whatever Google
       tag the header loads still gets the update. */
    window.dataLayer = window.dataLayer || [];
    function push() {
      window.dataLayer.push(arguments);
    }
    push('consent', 'update', payload);
  }

  function pushDataLayer(state, source) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'privacy_choices_updated',
      privacy_choices_source: source,
      privacy_gpc: gpcOn(),
      privacy_choices: {
        analytics: state.analytics,
        marketing: state.marketing,
        preferences: state.preferences,
        sale_of_data: state.sale_of_data
      }
    });
  }

  function hideCookieBanner() {
    try {
      var banner = document.getElementById('rs-cookie-banner');
      if (!banner || banner.hidden) return;
      banner.hidden = true;
      banner.classList.remove('is-visible', 'is-instant', 'is-hiding');
      document.documentElement.classList.remove('rs-cookie-open');
      document.documentElement.style.removeProperty('--rs-cookie-h');
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * A newer Save supersedes one that is still waiting on Shopify, so a late
   * callback from the earlier attempt cannot mirror a stale choice.
   */
  var saveGeneration = 0;

  /** Writes consent through Shopify, then mirrors it to gtag and the dataLayer. */
  function save(state, source, done) {
    var generation = ++saveGeneration;
    withApi(function (available) {
      if (generation !== saveGeneration) return;
      if (!available) {
        done(false);
        return;
      }

      var settled = false;
      var reportedSuccess = false;
      var mirrored = false;
      function stillCurrent() {
        return generation === saveGeneration;
      }
      /* The 4s timeout settles the button, but Shopify can confirm after
         that. Mirror that confirmation anyway, or this page keeps the old
         grant while Shopify has already stored the opt-out (#691). */
      function mirror() {
        if (mirrored || !stillCurrent()) return;
        mirrored = true;
        gtagUpdate(state);
        pushDataLayer(state, source);
        hideCookieBanner();
      }
      function finish(success) {
        if (!stillCurrent()) return;
        if (success) mirror();
        if (settled) {
          /* The timeout already showed the failure. A later confirm replaces
             it with the same success message a prompt confirm would have. */
          if (success && !reportedSuccess) {
            reportedSuccess = true;
            done(true);
          }
          return;
        }
        settled = true;
        reportedSuccess = !!success;
        done(success);
      }

      try {
        window.Shopify.customerPrivacy.setTrackingConsent(
          {
            analytics: state.analytics,
            marketing: state.marketing,
            preferences: state.preferences,
            sale_of_data: state.sale_of_data
          },
          function (result) {
            finish(!(result && result.error));
          }
        );
      } catch (e) {
        finish(false);
        return;
      }

      setTimeout(function () {
        finish(matchesStored(state));
      }, SAVE_TIMEOUT_MS);
    });
  }

  /* ------------------------------------------------------------- toggle UI */

  function inputsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-privacy-category]'));
  }

  function writeInputs(root, state) {
    inputsIn(root).forEach(function (input) {
      var key = input.getAttribute('data-privacy-category');
      if (typeof state[key] === 'boolean') input.checked = state[key];
    });
  }

  function readInputs(root) {
    var state = {};
    inputsIn(root).forEach(function (input) {
      state[input.getAttribute('data-privacy-category')] = !!input.checked;
    });
    CATEGORIES.forEach(function (key) {
      if (typeof state[key] !== 'boolean') state[key] = fallbackValue();
    });
    if (gpcOn()) {
      GPC_CATEGORIES.forEach(function (key) {
        state[key] = false;
      });
    }
    return state;
  }

  /** Under GPC the two opted-out categories are shown off and cannot be re-enabled. */
  function applyGpcLock(root) {
    var on = gpcOn();
    var notice = root.querySelector('[data-privacy-choices-gpc]');
    if (notice) notice.hidden = !on;
    if (!on) return;
    inputsIn(root).forEach(function (input) {
      if (GPC_CATEGORIES.indexOf(input.getAttribute('data-privacy-category')) === -1) return;
      input.checked = false;
      input.disabled = true;
    });
  }

  /* Approved status messages (copy review items 20-24). */
  var SALE_OPT_OUT_STATUS = 'You have opted out of the sale or sharing of your personal information.';

  /** GPC, or a sale/sharing choice Shopify already has stored as no. */
  function optedOutOfSaleSharing() {
    if (gpcOn()) return true;
    return storedConsent().sale_of_data === false;
  }

  function setStatus(root, message, isError) {
    var el = root.querySelector('[data-privacy-choices-status]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
    el.classList.toggle('privacy-choices__status--error', !!isError);
  }

  /** Shows the sale/sharing opt-out status, and clears it once that opt-out is gone. */
  function syncSaleOptOutStatus(root) {
    if (optedOutOfSaleSharing()) {
      setStatus(root, SALE_OPT_OUT_STATUS);
      return;
    }
    var el = root.querySelector('[data-privacy-choices-status]');
    if (el && el.textContent === SALE_OPT_OUT_STATUS) setStatus(root, '');
  }

  /** Save can only run once there is an API to write the choice through. */
  function setSaveEnabled(root, enabled) {
    var button = root.querySelector('[data-privacy-choices-save]');
    if (button) button.disabled = !enabled;
  }

  /** Toggles stay inert until the consent read that seeds them has finished. */
  function setInputsDisabled(root, disabled) {
    inputsIn(root).forEach(function (input) {
      input.disabled = disabled;
    });
  }

  /**
   * A later refresh supersedes one whose consent API is still loading, so a
   * slow callback cannot re-enable a panel that has since been re-opened.
   */
  var refreshGeneration = 0;

  function refresh(root) {
    var generation = ++refreshGeneration;
    var edited = false;
    function markEdited() {
      edited = true;
    }
    var inputs = inputsIn(root);
    inputs.forEach(function (input) {
      input.addEventListener('change', markEdited);
    });

    writeInputs(root, resolveState());
    applyGpcLock(root);
    /* Disabled until this read finishes. Otherwise a toggle the visitor
       flips while loadFeatures is in flight is overwritten by the callback
       (#690). Save stays disabled for the same wait. */
    setInputsDisabled(root, true);
    setSaveEnabled(root, false);
    syncSaleOptOutStatus(root);
    withApi(function (available) {
      inputs.forEach(function (input) {
        input.removeEventListener('change', markEdited);
      });
      if (generation !== refreshGeneration) return;
      if (!available) {
        setStatus(
          root,
          'We could not load your current settings. Please reload the page, or email help@residencesupply.com and we will process your request.',
          true
        );
        return;
      }
      /* A change that landed anyway wins over the late read. */
      if (!edited) writeInputs(root, resolveState());
      setInputsDisabled(root, false);
      applyGpcLock(root);
      setSaveEnabled(root, true);
      syncSaleOptOutStatus(root);
    });
  }

  /**
   * Wires the panel's toggles to its Save button. Shopify's managed opt-out
   * page serves /pages/data-sharing-opt-out, so the panel is the only surface
   * in the theme that writes consent.
   */
  function bindPanelActions(root) {
    if (!root || root.dataset.privacyChoicesBound === 'true') return;
    root.dataset.privacyChoicesBound = 'true';

    function commit(button, state, successMessage) {
      var label = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Saving…';
      }
      setStatus(root, '');
      save(state, 'panel', function (success) {
        if (button) {
          button.disabled = false;
          button.textContent = label;
        }
        writeInputs(root, resolveState());
        applyGpcLock(root);
        setStatus(
          root,
          success
            ? successMessage
            : 'We could not save your choices. Please reload the page, or email help@residencesupply.com and we will process your request.',
          !success
        );
      });
    }

    root.addEventListener('click', function (event) {
      var saveBtn = event.target.closest('[data-privacy-choices-save]');
      if (!saveBtn || saveBtn.disabled) return;
      commit(saveBtn, readInputs(root), 'Your choices have been saved.');
    });
  }

  /* ---------------------------------------------------------------- panel */

  var panel = {
    el: null,
    dialog: null,
    lastFocused: null,

    init: function () {
      this.el = document.querySelector('[data-privacy-choices]');
      if (!this.el) return;
      this.dialog = this.el.querySelector('.privacy-choices__dialog');
      bindPanelActions(this.el);

      var self = this;

      this.el.addEventListener('click', function (event) {
        if (event.target.closest('[data-privacy-choices-close]')) {
          event.preventDefault();
          self.close();
        }
      });

      document.addEventListener('keydown', function (event) {
        if (self.el.hidden) return;
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.preventDefault();
          self.close();
          return;
        }
        if (event.key === 'Tab') self.trap(event);
      });

      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-privacy-choices-open]');
        if (!trigger) return;
        event.preventDefault();
        self.open(trigger);
      });
    },

    focusable: function () {
      return Array.prototype.slice.call(this.dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    },

    trap: function (event) {
      var items = this.focusable();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    open: function (trigger) {
      this.lastFocused = trigger || document.activeElement;
      setStatus(this.el, '');
      this.el.hidden = false;
      document.documentElement.classList.add('privacy-choices-open');
      refresh(this.el);
      var items = this.focusable();
      if (items.length) items[0].focus();
    },

    close: function () {
      this.el.hidden = true;
      document.documentElement.classList.remove('privacy-choices-open');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
      this.lastFocused = null;
    }
  };

  /* ----------------------------------------------------------------- boot */

  function init() {
    try {
      panel.init();
    } catch (e) {
      /* The footer link falls back to Shopify's managed opt-out page at
         /pages/data-sharing-opt-out and the rest of the page is untouched
         (merge danger note in the spec). */
      if (window.console && console.warn) console.warn('privacy-choices:', e);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
